import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { RequestState } from '../../models/RequestState';
import {
    CoreClassroomAdminDashboard,
    CoreClassroomAdminDashboardOwnerCount,
    CoreClassroomAdminDashboardSchoolCount
} from '../../models/CoreClassroomAdminClassroom';
import StatusCode from '../../util/StatusCode';
import Loading from '../misc/Loading';
import TableHorizontal, { InfoObject } from '../misc/TableHorizontal';
import {
    getClassroomAdminDashboardInfo,
    getClassroomAdminDashboardOwners,
    getClassroomAdminDashboardSchools
} from '../../store/ClassroomAdmin/ClassroomAdminDashboardAction';

type CountSelector = (data: CoreClassroomAdminDashboard) => number;

interface Info {
    key: string
    name: string
    link: string
    selector: CountSelector
}

const ClassroomAdminDashboard = () => {
    const { t } = useTranslation(['common', 'classroomAdmin']);
    const dispatch = useDispatch();

    const dashboard = useSelector<RootState, RequestState<CoreClassroomAdminDashboard>>(x => x.classroomAdminDashboard.dashboard);
    const owners = useSelector<RootState, RequestState<CoreClassroomAdminDashboardOwnerCount[]>>(x => x.classroomAdminDashboard.owners);
    const schools = useSelector<RootState, RequestState<CoreClassroomAdminDashboardSchoolCount[]>>(x => x.classroomAdminDashboard.schools);

    useEffect(() => {
        getClassroomAdminDashboardInfo()(dispatch);
        getClassroomAdminDashboardOwners()(dispatch);
        getClassroomAdminDashboardSchools()(dispatch);
    }, [dispatch]);

    const info: Info[] = [
        { key: 'classrooms', name: t('classroomAdmin.dashboard.stats.classrooms', { ns: 'classroomAdmin' }), link: '/classroomadmin/classroom', selector: d => d.classrooms },
        { key: 'synched', name: t('classroomAdmin.dashboard.stats.synched', { ns: 'classroomAdmin' }), link: '/classroomadmin/classroom?syncState=1', selector: d => d.synched },
        { key: 'orphanRisk', name: t('classroomAdmin.dashboard.stats.orphanRisk', { ns: 'classroomAdmin' }), link: '/classroomadmin/classroom?status=1', selector: d => d.orphanRisk },
        { key: 'orphaned', name: t('classroomAdmin.dashboard.stats.orphaned', { ns: 'classroomAdmin' }), link: '/classroomadmin/classroom?status=2', selector: d => d.orphaned },
        { key: 'missingTeachers', name: t('classroomAdmin.dashboard.stats.missingTeachers', { ns: 'classroomAdmin' }), link: '/classroomadmin/classroom?missingTeachers=true', selector: d => d.missingTeachers }
    ];

    const getCount = (selector: CountSelector): number =>
        dashboard.code === StatusCode.COMPLETE && dashboard.data ? selector(dashboard.data) : 0;

    const ownerInfo: InfoObject[] =
        owners.code === StatusCode.COMPLETE
            ? owners.data.map((x, i) => ({ id: i.toString(), data: [{ value: x.owner || '-' }, { value: x.classrooms }] }))
            : [];

    const schoolInfo: InfoObject[] =
        schools.code === StatusCode.COMPLETE
            ? schools.data.map((x, i) => ({ id: i.toString(), data: [{ value: x.school }, { value: x.classrooms }] }))
            : [];

    const renderBody = () => {
        if (dashboard.code === StatusCode.PENDING) {
            return <Loading />;
        }
        if (dashboard.code === StatusCode.ERROR) {
            return <p>{t('error.getDashboardInfo', { ns: 'common' })}</p>;
        }

        return (
            <>
                <h3>{t('classroomAdmin.dashboard.countActive', { ns: 'classroomAdmin' })}</h3>
                <div className="list-group list-group-horizontal-lg">
                    {info.map(entry => (
                        <Link to={entry.link} key={entry.key} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center flex-fill">
                            {entry.name}
                            <span className="badge bg-primary rounded-pill">{getCount(entry.selector)}</span>
                        </Link>
                    ))}
                </div>

                <div className="double-col">
                    <div>
                        <h3 className="mt-3">{t('classroomAdmin.dashboard.ownerOverview', { ns: 'classroomAdmin' })}</h3>
                        <TableHorizontal
                            header={[
                                { value: t('classroomAdmin.dashboard.owner', { ns: 'classroomAdmin' }) },
                                { value: t('classroomAdmin.dashboard.classrooms', { ns: 'classroomAdmin' }) }
                            ]}
                            info={ownerInfo}
                            error={owners.code === StatusCode.ERROR}
                            searching={owners.code === StatusCode.PENDING}
                        />
                    </div>

                    <div>
                        <h3 className="mt-3">{t('classroomAdmin.dashboard.schoolOverview', { ns: 'classroomAdmin' })}</h3>
                        <TableHorizontal
                            header={[
                                { value: t('classroomAdmin.dashboard.school', { ns: 'classroomAdmin' }) },
                                { value: t('classroomAdmin.dashboard.classrooms', { ns: 'classroomAdmin' }) }
                            ]}
                            info={schoolInfo}
                            error={schools.code === StatusCode.ERROR}
                            searching={schools.code === StatusCode.PENDING}
                        />
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="container dashboard_oneroster padding-40">
            <h1>{t('classroomAdmin.dashboard.title', { ns: 'classroomAdmin' })}</h1>
            {renderBody()}
        </div>
    );
};

export default ClassroomAdminDashboard;